import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, IconButton, Typography, Box,
  MenuItem, Select, FormControl, InputLabel, FormHelperText,
  CircularProgress, Stack, Chip,
  Autocomplete, alpha,
} from '@mui/material';
import CloseIcon          from '@mui/icons-material/Close';
import SaveIcon           from '@mui/icons-material/Save';
import PlayArrowIcon      from '@mui/icons-material/PlayArrow';
import EventNoteIcon      from '@mui/icons-material/EventNote';
import TuneIcon           from '@mui/icons-material/Tune';
import BusinessIcon       from '@mui/icons-material/Business';
import CalendarMonthIcon  from '@mui/icons-material/CalendarMonth';
import CheckBoxIcon       from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import AutoAwesomeIcon    from '@mui/icons-material/AutoAwesome';
import { fetchRecords, createRecord, updateRecord, generatePayPeriods } from '../services/api';
import { useSnackbar } from 'notistack';
import { useApp } from '../context/AppContext';
import { PATTERNS, TABLE_CONFIG, generatePK } from '../config/fieldConfig';

const GREEN   = '#22c55e';
const GREEN_D = '#16a34a';
const GREEN_L = '#f0fdf4';
const GREEN_B = '#bbf7d0';

const ALL_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const DAY_LABEL = { MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun' };

const PERIOD_STATUS_META = {
  pending:   { color: '#D97706', bg: '#fffbeb', border: '#fde68a',  label: 'Pending'   },
  processed: { color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe',  label: 'Processed' },
  paid:      { color: GREEN,     bg: GREEN_L,   border: GREEN_B,    label: 'Paid'      },
};

const RadioDot = ({ active }) => (
  <Box sx={{
    width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
    border: `2px solid ${active ? GREEN : '#CBD5E1'}`,
    bgcolor: active ? GREEN : 'transparent',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.15s',
  }}>
    {active && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'white' }} />}
  </Box>
);

const getFiscalYear = (month, year) => {
  if (month >= 4) return `${year}-${String(year + 1).slice(2)}`;
  return `${year - 1}-${String(year).slice(2)}`;
};

const SectionHeader = ({ icon, label, color = GREEN }) => (
  <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 2 }}>
    <Box sx={{
      width: 30, height: 30, borderRadius: '8px',
      bgcolor: alpha(color, 0.12),
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      {React.cloneElement(icon, { sx: { fontSize: 16, color } })}
    </Box>
    <Typography fontSize={12.5} fontWeight={700} color="text.secondary" letterSpacing={0.6}
      sx={{ textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', bgcolor: '#E8EDF5' }} />
  </Stack>
);

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GREEN },
  },
  '& label.Mui-focused': { color: GREEN },
};

const EMPTY = {
  payroll_name: '',
  payroll_code: '',
  period_type: 'monthly',
  currency_code: 'INR',
  cutoff_day: 25,
  payment_type: 'fixed_day',
  payment_day: 30,
  payment_month_type: 'current_month',
  working_days: ['MON', 'TUE', 'WED', 'THU', 'FRI'],
  salary_calculation_type: 'actual_days',
  status: 'active',
  COMPANY_id: '',
  bg_id: '',
  ldg_id: 'LDG001',
  consolidation_group_id: '',
  first_period_end_date: '',
  payment_date_offset: 0,
  number_of_years: 5,
  active_flag: 'Y',
  effective_from: new Date().toISOString().split('T')[0],
  effective_to: '2099-12-31',
  created_by: 'SYSTEM',
};

export default function PayScheduleForm({ open, onClose, onSave, record, existingRecords = [] }) {
  const isEdit = !!record;
  const { enqueueSnackbar } = useSnackbar();
  const { state: appState } = useApp();
  const activeContext = appState?.activeContext || null;
  const [form, setForm]             = useState(EMPTY);
  const [errors, setErrors]         = useState({});
  const [saving, setSaving]         = useState(false);
  const [generating, setGenerating] = useState(false);
  const [companies, setCompanies]   = useState([]);
  const [bizGroups, setBizGroups]   = useState([]);
  const [conGroups, setConGroups]   = useState([]);
  const [periodTypes, setPeriodTypes] = useState([]);
  const [loadingFKs, setLoadingFKs] = useState(false);
  const [latestPeriod, setLatestPeriod] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoadingFKs(true);
    Promise.all([
      fetchRecords('COMPANY'),
      fetchRecords('BUSINESS_GROUP'),
      fetchRecords('CONSOLIDATION_GROUP'),
      fetchRecords('PAY_PERIOD'),
      fetchRecords('PERIOD_TYPE'),
    ]).then(([c, b, cg, pp, pt]) => {
      setCompanies(c.data?.data || []);
      setBizGroups(b.data?.data || []);
      setConGroups(cg.data?.data || []);
      setPeriodTypes(pt.data?.data || []);
      if (record) {
        const periods = (pp.data?.data || [])
          .filter(p => p.payroll_id === record.payroll_id && p.status === 'pending')
          .sort((a, b) => a.period_name.localeCompare(b.period_name));
        setLatestPeriod(periods[0] || null);
      }
    }).catch(() => {}).finally(() => setLoadingFKs(false));
  }, [open, record]);

  // Map of period_code → period_name for dynamic visibility checks.
  // Use lowercase comparison so existing "monthly"/"weekly" data continues to match.
  const normalizedPeriodType = String(form.period_type || '').toLowerCase().replace(/[\s-]/g, '_');
  const isMonthly      = normalizedPeriodType === 'monthly' || normalizedPeriodType === 'semi_monthly';
  const isWeeklyish    = normalizedPeriodType === 'weekly' || normalizedPeriodType === 'biweekly' || normalizedPeriodType === 'bi_weekly';
  const isDaily        = normalizedPeriodType === 'daily';

  useEffect(() => {
    if (open) {
      let base = record ? { ...EMPTY, ...record } : { ...EMPTY };
      // Auto-fill from active payroll context for new records
      if (!record && activeContext) {
        if (activeContext.company_id) base.COMPANY_id = activeContext.company_id;
        if (activeContext.bg_id)      base.bg_id      = activeContext.bg_id;
        if (activeContext.ldg_id)     base.ldg_id     = activeContext.ldg_id;
      }
      if (base.working_days && typeof base.working_days === 'string') {
        try { base.working_days = JSON.parse(base.working_days); } catch { base.working_days = ['MON','TUE','WED','THU','FRI']; }
      }
      setForm(base);
      setErrors({});
    }
  }, [open, record, activeContext]);

  useEffect(() => {
    if (!open) return;
    if (form.payment_type === 'fixed_day' && form.payment_day) {
      if (form.payment_month_type === 'current_month' && form.payment_day < 28) {
        setErrors(prev => ({ ...prev, payment_day: 'For Same Month payment, please select a payment day of 28 or later.' }));
      } else if (form.payment_day < 1 || form.payment_day > 31) {
        setErrors(prev => ({ ...prev, payment_day: 'Payment day must be between 1 and 31' }));
      } else {
        setErrors(prev => {
          if (prev.payment_day === 'For Same Month payment, please select a payment day of 28 or later.' ||
              prev.payment_day === 'Payment day must be between 1 and 31') {
            return { ...prev, payment_day: '' };
          }
          return prev;
        });
      }
    }
  }, [open, form.payment_type, form.payment_day, form.payment_month_type]);

  const set = (field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      // Cascade: changing Business Group clears the dependent Company selection and Consolidation Group
      if (field === 'bg_id') {
        if (prev.COMPANY_id) next.COMPANY_id = '';
        if (prev.consolidation_group_id) next.consolidation_group_id = '';
      }
      // Cascade: changing Company clears the dependent Consolidation Group selection
      if (field === 'COMPANY_id') {
        if (prev.consolidation_group_id) next.consolidation_group_id = '';
      }
      // Auto-generate payroll_code from payroll_name if it matches previous auto-gen code or is empty
      if (field === 'payroll_name') {
        const oldGenerated = prev.payroll_name
          ? prev.payroll_name.replace(/[^A-Za-z0-9\s]/g, '').trim().replace(/\s+/g, '_').toUpperCase().slice(0, 20)
          : '';
        const currentCode = prev.payroll_code || '';
        if (!currentCode || currentCode === oldGenerated) {
          next.payroll_code = value
            .replace(/[^A-Za-z0-9\s]/g, '')
            .trim()
            .replace(/\s+/g, '_')
            .toUpperCase()
            .slice(0, 20);
        }
      }
      return next;
    });
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const toggleDay = (day) => {
    setForm(prev => {
      const days = Array.isArray(prev.working_days) ? prev.working_days : [];
      return {
        ...prev,
        working_days: days.includes(day) ? days.filter(d => d !== day) : [...days, day],
      };
    });
  };

  const validate = () => {
    const e = {};
    if (!form.payroll_name)                                e.payroll_name = 'Payroll name is required';
    else if (!new RegExp(PATTERNS.ALPHANUMERIC.pattern).test(form.payroll_name))
                                                           e.payroll_name = PATTERNS.ALPHANUMERIC.patternMsg;
    if (!form.payroll_code)                                e.payroll_code = 'Payroll code is required';
    else if (!/^[A-Z][A-Z0-9_-]{1,19}$/.test(form.payroll_code))
                                                           e.payroll_code = 'Uppercase letters/digits/hyphens/underscores, must start with letter (max 20 chars)';
    if (!form.period_type)           e.period_type  = 'Type is required';
    // Cutoff Day — required, integer, range 1-31 (only enforced when monthly/semi-monthly schedule shows the field)
    if (isMonthly || isWeeklyish) {
      const cd = form.cutoff_day;
      if (cd === '' || cd === null || cd === undefined || Number.isNaN(Number(cd))) {
        e.cutoff_day = 'Cutoff day is required';
      } else if (!Number.isInteger(Number(cd))) {
        e.cutoff_day = 'Cutoff day must be a whole number';
      } else if (Number(cd) < 1 || Number(cd) > 31) {
        e.cutoff_day = 'Cutoff day must be between 1 and 31';
      }
    }
    if (!form.payment_type)          e.payment_type = 'Payment type is required';
    if (form.payment_type === 'fixed_day') {
      if (!form.payment_day) {
        e.payment_day = 'Payment day is required for fixed day';
      } else if (form.payment_month_type === 'current_month' && form.payment_day < 28) {
        e.payment_day = 'For Same Month payment, please select a payment day of 28 or later.';
      } else if (form.payment_day < 1 || form.payment_day > 31) {
        e.payment_day = 'Payment day must be between 1 and 31';
      }
    }
    if (!form.payment_month_type)    e.payment_month_type = 'Payment month is required';
    if (!form.bg_id)                 e.bg_id        = 'Business Group is required';
    if (!form.COMPANY_id)            e.COMPANY_id   = 'Company is required';
    if (!form.first_period_end_date) e.first_period_end_date = 'Start payroll from is required';
    if (!form.working_days?.length)  e.working_days = 'Select at least one working day';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        payroll_id: isEdit ? form.payroll_id : (form.payroll_id || generatePK(TABLE_CONFIG.PAYROLL, existingRecords, form.COMPANY_id || '')),
        working_days: Array.isArray(form.working_days)
          ? form.working_days
          : ['MON','TUE','WED','THU','FRI'],
      };
      await onSave(payload);
    } catch (e) {
      // Ignore to prevent unhandled promise rejection. 
      // Error is already shown in TablePage via snackbar.
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePayPeriods = async () => {
    setGenerating(true);
    try {
      const res = await generatePayPeriods(form.payroll_id);
      const count = res.data?.data?.length || 0;
      enqueueSnackbar(count > 0 ? `${count} pay period(s) generated` : 'All periods already exist', {
        variant: count > 0 ? 'success' : 'info',
      });
      const pp = await fetchRecords('PAY_PERIOD');
      const periods = (pp.data?.data || [])
        .filter(p => p.payroll_id === form.payroll_id && p.status === 'pending')
        .sort((a, b) => a.period_name.localeCompare(b.period_name));
      setLatestPeriod(periods[0] || null);
    } catch {
      enqueueSnackbar('Failed to generate pay periods', { variant: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const allCompanyOpts = companies.map(r => ({ value: r.company_id, label: `${r.company_id} - ${r.company_name || r.company_id}`, _row: r }));
  const bgOpts         = bizGroups.map(r => ({ value: r.bg_id,      label: `${r.bg_id} - ${r.business_group_name || r.bg_id}` }));
  // Filter companies by selected Business Group (matches generic form pattern)
  let companyOpts = form.bg_id
    ? allCompanyOpts.filter(o => o._row.default_bg_id === form.bg_id)
    : [];
  // Tolerant fallback: keep an existing Company visible even if it doesn't match the BG filter
  if (form.COMPANY_id && !companyOpts.find(o => o.value === form.COMPANY_id)) {
    const legacy = allCompanyOpts.find(o => o.value === form.COMPANY_id);
    if (legacy) companyOpts = [...companyOpts, legacy];
  }

  const allCgOpts = conGroups.map(r => ({
    value: r.consolidation_group_id,
    label: `${r.consolidation_group_id} - ${r.group_name || ''}`,
    _row: r
  }));
  // Filter Consolidation Groups by selected Business Group and Company combination
  let cgOpts = (form.bg_id && form.COMPANY_id)
    ? allCgOpts.filter(o => o._row.bg_id === form.bg_id && o._row.COMPANY_id === form.COMPANY_id)
    : [];
  // Tolerant fallback: keep an existing Consolidation Group visible even if it doesn't match the filters
  if (form.consolidation_group_id && !cgOpts.find(o => o.value === form.consolidation_group_id)) {
    const legacy = allCgOpts.find(o => o.value === form.consolidation_group_id);
    if (legacy) cgOpts = [...cgOpts, legacy];
  }

  const selectedDays = Array.isArray(form.working_days) ? form.working_days : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.18)' } }}
    >
      {/* Header */}
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${GREEN_D} 0%, ${GREEN} 100%)`,
          px: 3, py: 2.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{
              width: 40, height: 40, borderRadius: '12px',
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <EventNoteIcon sx={{ fontSize: 22, color: 'white' }} />
            </Box>
            <Box>
              <Typography fontWeight={800} fontSize={16} color="white" lineHeight={1.2}>
                {isEdit ? 'Edit Pay Schedule' : 'New Pay Schedule'}
              </Typography>
              <Typography fontSize={11} sx={{ color: 'rgba(255,255,255,0.75)' }}>
                Configure payroll schedule, working days &amp; calculation rules
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small"
            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: '#F8FAFC' }}>
        {loadingFKs ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} sx={{ color: GREEN }} />
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>

            {/* Section 1: Organisation */}
            <Box sx={{ mb: 3, p: 2.5, bgcolor: 'white', borderRadius: '14px', border: '1.5px solid #E8EDF5' }}>
              <SectionHeader icon={<BusinessIcon />} label="Organisation" />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    size="small"
                    options={bgOpts}
                    value={bgOpts.find(o => o.value === form.bg_id) || null}
                    onChange={(_, v) => set('bg_id', v?.value || '')}
                    renderInput={params => (
                      <TextField {...params} label="Business Group *" error={!!errors.bg_id}
                        helperText={errors.bg_id} sx={inputSx} />
                    )}
                    isOptionEqualToValue={(o, v) => o.value === v.value}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    size="small"
                    options={companyOpts}
                    disabled={!form.bg_id}
                    value={companyOpts.find(o => o.value === form.COMPANY_id) || null}
                    onChange={(_, v) => set('COMPANY_id', v?.value || '')}
                    noOptionsText={!form.bg_id ? 'Select a Business Group first' : 'No options'}
                    renderInput={params => (
                      <TextField {...params} label="Company *" error={!!errors.COMPANY_id}
                        helperText={errors.COMPANY_id || (!form.bg_id ? 'Select a Business Group first' : '')}
                        sx={inputSx} />
                    )}
                    isOptionEqualToValue={(o, v) => o.value === v.value}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    size="small"
                    options={cgOpts}
                    disabled={!form.bg_id || !form.COMPANY_id}
                    value={cgOpts.find(o => o.value === form.consolidation_group_id) || null}
                    onChange={(_, v) => set('consolidation_group_id', v?.value || '')}
                    noOptionsText={!form.bg_id ? 'Select a Business Group first' : (!form.COMPANY_id ? 'Select a Company first' : 'No options')}
                    renderInput={params => (
                      <TextField 
                        {...params} 
                        label="Consolidation Group" 
                        helperText={!form.bg_id ? 'Select a Business Group first' : (!form.COMPANY_id ? 'Select a Company first' : '')}
                        sx={inputSx} 
                      />
                    )}
                    isOptionEqualToValue={(o, v) => o.value === v.value}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    size="small" fullWidth type="date"
                    label="Start Payroll From *"
                    value={form.first_period_end_date}
                    onChange={e => set('first_period_end_date', e.target.value)}
                    error={!!errors.first_period_end_date}
                    helperText={errors.first_period_end_date || 'First period end date'}
                    InputLabelProps={{ shrink: true }}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl size="small" fullWidth sx={inputSx}>
                    <InputLabel>Status</InputLabel>
                    <Select value={form.status} label="Status" onChange={e => set('status', e.target.value)}>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            {/* Section 1: Pay Schedule Identity */}
            <Box sx={{ mb: 3, p: 2.5, bgcolor: 'white', borderRadius: '14px', border: '1.5px solid #E8EDF5' }}>
              <SectionHeader icon={<TuneIcon />} label="Pay Schedule" />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small" fullWidth
                    label="Payroll Name *"
                    value={form.payroll_name}
                    onChange={e => set('payroll_name', e.target.value)}
                    error={!!errors.payroll_name}
                    helperText={errors.payroll_name || 'e.g. Monthly Payroll'}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small" fullWidth
                    label="Payroll Code *"
                    value={form.payroll_code}
                    onChange={e => set('payroll_code', e.target.value.toUpperCase())}
                    error={!!errors.payroll_code}
                    helperText={errors.payroll_code || 'Auto-generated or custom code'}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl size="small" fullWidth error={!!errors.period_type} sx={inputSx}>
                    <InputLabel>Period Type *</InputLabel>
                    <Select value={form.period_type} label="Period Type *" onChange={e => set('period_type', e.target.value)}>
                      {periodTypes.length === 0 && (
                        <MenuItem value="" disabled><em>Loading from PERIOD_TYPE master…</em></MenuItem>
                      )}
                      {periodTypes
                        .filter(pt => pt.active_flag !== 'N')
                        .sort((a, b) => (a.number_of_days || 0) - (b.number_of_days || 0))
                        .map(pt => {
                          const valLower = String(pt.period_code || pt.period_name || '').toLowerCase().replace(/[\s-]/g, '_');
                          return (
                            <MenuItem key={pt.period_type_id} value={valLower}>
                              {pt.period_name} {pt.number_of_days ? `(${pt.number_of_days} days)` : ''}
                            </MenuItem>
                          );
                        })}
                    </Select>
                    {errors.period_type && <FormHelperText>{errors.period_type}</FormHelperText>}
                    {!errors.period_type && (
                      <FormHelperText sx={{ fontSize: 11 }}>
                        Sourced from PERIOD_TYPE master. Schedule fields below adapt to selection.
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                {(isMonthly || isWeeklyish) && <Grid item xs={12} sm={3}>
                  <TextField
                    size="small" fullWidth type="number"
                    label={isMonthly ? 'Cutoff Day *' : 'Cycle End Day *'}
                    value={form.cutoff_day ?? ''}
                    onChange={e => {
                      const raw = e.target.value;
                      // Keep state as '' or a finite number — never NaN.
                      if (raw === '') return set('cutoff_day', '');
                      const n = Number(raw);
                      set('cutoff_day', Number.isFinite(n) ? n : '');
                    }}
                    error={!!errors.cutoff_day}
                    helperText={errors.cutoff_day || (isMonthly ? 'Day of month attendance closes (1-31)' : 'Day cycle ends (1-31)')}
                    inputProps={{ min: 1, max: 31, step: 1 }}
                    sx={inputSx}
                  />
                </Grid>}
                {/* Payment Configuration — Monthly / Semi-Monthly only */}
                {isMonthly && <Grid item xs={12}>
                  <Box sx={{
                    p: 2, borderRadius: '12px',
                    border: `1.5px solid ${errors.payment_type || errors.payment_day || errors.payment_month_type ? '#fca5a5' : '#E8EDF5'}`,
                    bgcolor: '#FAFBFC',
                  }}>
                    {/* Payment Date row */}
                    <Typography fontSize={11.5} fontWeight={700} color="text.secondary"
                      letterSpacing={0.5} textTransform="uppercase" mb={1.5}>
                      Payment Date
                    </Typography>
                    <Stack spacing={1}>
                      {/* Option A — Last working day */}
                      <Box
                        onClick={() => { set('payment_type', 'last_working_day'); set('payment_day', null); }}
                        sx={{
                          px: 2, py: 1.3, borderRadius: '10px', cursor: 'pointer',
                          border: `1.5px solid ${form.payment_type === 'last_working_day' ? GREEN : '#E2E8F0'}`,
                          bgcolor: form.payment_type === 'last_working_day' ? GREEN_L : 'white',
                          display: 'flex', alignItems: 'center', gap: 1.5,
                          transition: 'all 0.15s',
                          '&:hover': { borderColor: GREEN },
                        }}
                      >
                        <RadioDot active={form.payment_type === 'last_working_day'} />
                        <Typography fontSize={13} fontWeight={600}
                          color={form.payment_type === 'last_working_day' ? GREEN_D : '#374151'}>
                          Last working day of every month
                        </Typography>
                      </Box>

                      {/* Option B — Fixed day */}
                      <Box
                        onClick={() => set('payment_type', 'fixed_day')}
                        sx={{
                          px: 2, py: 1.3, borderRadius: '10px', cursor: 'pointer',
                          border: `1.5px solid ${form.payment_type === 'fixed_day' ? GREEN : '#E2E8F0'}`,
                          bgcolor: form.payment_type === 'fixed_day' ? GREEN_L : 'white',
                          display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
                          transition: 'all 0.15s',
                          '&:hover': { borderColor: GREEN },
                        }}
                      >
                        <RadioDot active={form.payment_type === 'fixed_day'} />
                        <Typography fontSize={13} fontWeight={600}
                          color={form.payment_type === 'fixed_day' ? GREEN_D : '#374151'}>
                          Day
                        </Typography>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="e.g. 30"
                          value={form.payment_day ?? ''}
                          onClick={e => e.stopPropagation()}
                          onChange={e => set('payment_day', e.target.value === '' ? null : Number(e.target.value))}
                          disabled={form.payment_type !== 'fixed_day'}
                          error={!!errors.payment_day}
                          inputProps={{ min: form.payment_month_type === 'current_month' ? 28 : 1, max: 31 }}
                          sx={{ width: 90, ...inputSx }}
                        />
                        <Typography fontSize={13} color="#6B7280">of every month</Typography>
                        {errors.payment_day && (
                          <Typography fontSize={11} color="error" width="100%" mt={0.3} pl={3.5}>
                            {errors.payment_day}
                          </Typography>
                        )}
                      </Box>
                    </Stack>

                    {/* Payment Month row */}
                    <Typography fontSize={11.5} fontWeight={700} color="text.secondary"
                      letterSpacing={0.5} textTransform="uppercase" mt={2} mb={1.5}>
                      Payment Month
                    </Typography>
                    <Stack direction="row" spacing={1.5}>
                      {[
                        { value: 'current_month', label: 'Same Month',  hint: 'e.g. April salary paid in April' },
                        { value: 'next_month',    label: 'Next Month',  hint: 'e.g. April salary paid in May'   },
                      ].map(opt => (
                        <Box
                          key={opt.value}
                          onClick={() => set('payment_month_type', opt.value)}
                          sx={{
                            flex: 1, px: 2, py: 1.3, borderRadius: '10px', cursor: 'pointer',
                            border: `1.5px solid ${form.payment_month_type === opt.value ? GREEN : '#E2E8F0'}`,
                            bgcolor: form.payment_month_type === opt.value ? GREEN_L : 'white',
                            display: 'flex', alignItems: 'flex-start', gap: 1.2,
                            transition: 'all 0.15s',
                            '&:hover': { borderColor: GREEN },
                          }}
                        >
                          <RadioDot active={form.payment_month_type === opt.value} sx={{ mt: 0.3 }} />
                          <Box>
                            <Typography fontSize={13} fontWeight={600}
                              color={form.payment_month_type === opt.value ? GREEN_D : '#374151'}>
                              {opt.label}
                            </Typography>
                            <Typography fontSize={10.5} color="text.secondary">{opt.hint}</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                    {form.payment_month_type && (
                      <Typography fontSize={11.5} color="text.secondary" mt={1.5} sx={{ display: 'block' }}>
                        {form.payment_month_type === 'current_month'
                          ? 'Salary will be disbursed on the selected day within the same month. The payment day must be 28 or later to allow sufficient payroll processing time.'
                          : 'Salary will be disbursed on the selected day of the following month. You may select any valid day (1–31).'}
                      </Typography>
                    )}
                  </Box>
                </Grid>}

                {/* Salary Calculation — hidden for Daily (each day fully worked = full pay) */}
                {!isDaily && <Grid item xs={12} sm={6}>
                  <FormControl size="small" fullWidth sx={inputSx}>
                    <InputLabel>Salary Calculation *</InputLabel>
                    <Select
                      value={form.salary_calculation_type}
                      label="Salary Calculation *"
                      onChange={e => set('salary_calculation_type', e.target.value)}
                    >
                      <MenuItem value="actual_days">Actual Days (calendar)</MenuItem>
                      <MenuItem value="working_days">Working Days only</MenuItem>
                    </Select>
                    <FormHelperText>Per-day salary basis</FormHelperText>
                  </FormControl>
                </Grid>}

                {/* Payment Date Offset — Weekly/Bi-Weekly only */}
                {isWeeklyish && <Grid item xs={12} sm={6}>
                  <TextField
                    size="small" fullWidth type="number"
                    label="Payment Date Offset (days)"
                    value={form.payment_date_offset ?? 0}
                    onChange={e => set('payment_date_offset', Number(e.target.value))}
                    inputProps={{ min: 0, max: 30 }}
                    helperText="Days after cycle-end when payment is made (typical: 2-5)"
                    sx={inputSx}
                  />
                </Grid>}
              </Grid>
            </Box>

            {/* Section 2: Working Days — hidden for Daily only */}
            {!isDaily && (
              <Box sx={{ mb: 3, p: 2.5, bgcolor: 'white', borderRadius: '14px', border: `1.5px solid ${errors.working_days ? '#fca5a5' : '#E8EDF5'}` }}>
              <SectionHeader icon={<CheckBoxIcon />} label="Working Days" />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {ALL_DAYS.map(day => {
                  const active = selectedDays.includes(day);
                  return (
                    <Box
                      key={day}
                      onClick={() => toggleDay(day)}
                      sx={{
                        width: 52, height: 52,
                        borderRadius: '12px',
                        border: `2px solid ${active ? GREEN : '#E2E8F0'}`,
                        bgcolor: active ? GREEN_L : 'white',
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: GREEN, bgcolor: alpha(GREEN, 0.06) },
                      }}
                    >
                      <Typography fontSize={11} fontWeight={700} color={active ? GREEN_D : '#94A3B8'}>
                        {DAY_LABEL[day]}
                      </Typography>
                      {active
                        ? <CheckBoxIcon sx={{ fontSize: 14, color: GREEN, mt: 0.3 }} />
                        : <CheckBoxOutlineBlankIcon sx={{ fontSize: 14, color: '#CBD5E1', mt: 0.3 }} />
                      }
                    </Box>
                  );
                })}
              </Stack>
              {errors.working_days && (
                <Typography fontSize={11} color="error" mt={1}>{errors.working_days}</Typography>
              )}
              <Typography fontSize={11} color="text.secondary" mt={1}>
                Selected: {selectedDays.length > 0 ? selectedDays.join(', ') : 'None'}
                &nbsp;·&nbsp;Used to dynamically calculate per-month working days
              </Typography>
            </Box>)}

            {/* Section 4: Latest Pending Pay Period */}
            {isEdit && (
              <Box sx={{ p: 2.5, bgcolor: 'white', borderRadius: '14px', border: '1.5px solid #E8EDF5' }}>
                <SectionHeader icon={<CalendarMonthIcon />} label="Latest Pay Period" />
                {latestPeriod ? (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                    <Box sx={{
                      flex: 1, p: 2, borderRadius: '12px',
                      bgcolor: PERIOD_STATUS_META.pending.bg,
                      border: `1.5px solid ${PERIOD_STATUS_META.pending.border}`,
                    }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography fontWeight={700} fontSize={15} color="#1E293B">
                          {latestPeriod.period_name}
                        </Typography>
                        <Chip
                          label={PERIOD_STATUS_META[latestPeriod.status]?.label || latestPeriod.status}
                          size="small"
                          sx={{
                            fontWeight: 700, fontSize: 11, borderRadius: '8px',
                            bgcolor: PERIOD_STATUS_META[latestPeriod.status]?.bg,
                            color: PERIOD_STATUS_META[latestPeriod.status]?.color,
                            border: `1px solid ${PERIOD_STATUS_META[latestPeriod.status]?.border}`,
                          }}
                        />
                      </Stack>
                      <Stack direction="row" spacing={3} flexWrap="wrap">
                        <Box>
                          <Typography fontSize={10} color="text.secondary" fontWeight={600} textTransform="uppercase">Fiscal Year</Typography>
                          <Typography fontSize={13} fontWeight={600} color="#374151">{latestPeriod.fiscal_year}</Typography>
                        </Box>
                        <Box>
                          <Typography fontSize={10} color="text.secondary" fontWeight={600} textTransform="uppercase">Period</Typography>
                          <Typography fontSize={13} fontWeight={600} color="#374151">{latestPeriod.start_date} → {latestPeriod.end_date}</Typography>
                        </Box>
                        <Box>
                          <Typography fontSize={10} color="text.secondary" fontWeight={600} textTransform="uppercase">Payment Date</Typography>
                          <Typography fontSize={13} fontWeight={600} color="#374151">{latestPeriod.payment_date}</Typography>
                        </Box>
                      </Stack>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={generating ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <PlayArrowIcon />}
                      onClick={handleGeneratePayPeriods}
                      disabled={generating}
                      sx={{
                        borderRadius: '10px', fontWeight: 700, fontSize: 13, px: 2.5, py: 1.2, mt: { xs: 0, sm: 0.5 },
                        background: `linear-gradient(135deg, ${GREEN_D}, ${GREEN})`,
                        boxShadow: `0 4px 14px ${alpha(GREEN, 0.4)}`,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {generating ? 'Running…' : 'Create Pay Run'}
                    </Button>
                  </Stack>
                ) : (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                    <Typography fontSize={13} color="text.secondary" sx={{ flex: 1 }}>
                      No pending pay period found for this payroll.
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={generating ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                      onClick={handleGeneratePayPeriods}
                      disabled={generating}
                      sx={{
                        borderRadius: '10px', fontWeight: 700, fontSize: 13,
                        borderColor: GREEN, color: GREEN,
                        '&:hover': { bgcolor: GREEN_L },
                      }}
                    >
                      {generating ? 'Generating…' : 'Generate Pay Period'}
                    </Button>
                  </Stack>
                )}
              </Box>
            )}

          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'white', borderTop: '1.5px solid #E8EDF5', gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: '10px', fontWeight: 600, fontSize: 13,
            borderColor: '#E2E8F0', color: '#64748B', mr: 'auto',
            '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SaveIcon />}
          variant="contained"
          sx={{
            borderRadius: '10px', fontWeight: 700, fontSize: 13,
            background: `linear-gradient(135deg, ${GREEN_D}, ${GREEN})`,
            boxShadow: `0 4px 14px ${alpha(GREEN, 0.4)}`,
            '&:hover': { background: `linear-gradient(135deg, #15803d, ${GREEN_D})` },
          }}
        >
          {saving ? 'Saving…' : (isEdit ? 'Update Schedule' : 'Create Schedule')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
